/**
 * Consolidated Iframe Detection Hooks with Redux
 *
 * This file contains all iframe-related hooks and selectors.
 * Core utilities are in src/utils/iframeContext.ts
 */

import { useEffect, useCallback } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { AppState } from "@Store/index";
import { detectIframeContextClient, IframeState } from "../app/iframeReducer";
import {
  navigateInIframe as navigateInIframeUtil,
  persistIframeSource,
} from "../utils/iframeContext";

// ============================================
// HOOKS
// ============================================

/**
 * Hook to get just the isIframe boolean (most common use case)
 */
export const useIsIframe = (): IframeState => {
  const context = useSelector(
    ({ iframeData }: AppState) => ({
      isIframe: iframeData.isIframe,
      parentOrigin: iframeData.parentOrigin,
      referer: iframeData.referer,
      canAccessParent: iframeData.canAccessParent,
      isCrossOrigin: iframeData.isCrossOrigin,
      isDetected: iframeData.isDetected,
      partnerDomain: iframeData.partnerDomain,
      partnerId: iframeData.partnerId,
      iframeSource: iframeData.iframeSource,
    }),
    shallowEqual,
  );

  return { ...context };
};

/**
 * Enhanced hook for iframe detection with full context using Redux
 * Uses shallowEqual for optimized re-renders
 */
export const useIframeDetection = (): IframeState => {
  const dispatch = useDispatch();

  const context = useSelector(
    ({ iframeData }: AppState) => ({
      isIframe: iframeData.isIframe,
      parentOrigin: iframeData.parentOrigin,
      referer: iframeData.referer,
      canAccessParent: iframeData.canAccessParent,
      isCrossOrigin: iframeData.isCrossOrigin,
      isDetected: iframeData.isDetected,
      partnerDomain: iframeData.partnerDomain,
      partnerId: iframeData.partnerId,
      iframeSource: iframeData.iframeSource,
    }),
    shallowEqual,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (!context.isDetected) {
      dispatch(detectIframeContextClient());
    } else {
      persistIframeSource();
    }
  }, [dispatch, context.isDetected]);

  return context;
};

/**
 * Hook to get partner information
 * Uses shallowEqual for optimized re-renders
 */
export const usePartnerInfo = () =>
  useSelector(
    ({ iframeData }: AppState) => ({
      partnerDomain: iframeData.partnerDomain,
      partnerId: iframeData.partnerId,
      referer: iframeData.referer,
      iframeSource: iframeData.iframeSource,
    }),
    shallowEqual,
  );

/**
 * Hook for iframe-aware navigation using Redux
 * Uses shallowEqual for optimized re-renders
 */
export const useIframeNavigation = () => {
  const { isIframe, canAccessParent, isCrossOrigin } = useSelector(
    ({ iframeData }: AppState) => ({
      isIframe: iframeData.isIframe,
      canAccessParent: iframeData.canAccessParent,
      isCrossOrigin: iframeData.isCrossOrigin,
    }),
    shallowEqual,
  );

  const navigate = useCallback(
    (url: string, options?: { openInParent?: boolean; forceNewTab?: boolean }) => {
      if (options?.forceNewTab) {
        window.open(url, "_blank");
        return;
      }

      if (isIframe) {
        navigateInIframeUtil(url, options?.openInParent ?? true);
      } else {
        window.location.href = url;
      }
    },
    [isIframe],
  );

  const openInNewTab = useCallback((url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  const navigateParent = useCallback(
    (url: string) => {
      if (isIframe && canAccessParent) {
        try {
          window.parent.location.href = url;
        } catch (e) {
          openInNewTab(url);
        }
      } else if (isIframe) {
        openInNewTab(url);
      } else {
        window.location.href = url;
      }
    },
    [isIframe, canAccessParent, openInNewTab],
  );

  return {
    navigate,
    openInNewTab,
    navigateParent,
    isIframe,
    canAccessParent,
    isCrossOrigin,
  };
};

/**
 * Hook for postMessage communication with parent window
 * Uses shallowEqual for optimized re-renders
 */
export const useIframeMessaging = () => {
  const { isIframe, isCrossOrigin } = useSelector(
    ({ iframeData }: AppState) => ({
      isIframe: iframeData.isIframe,
      isCrossOrigin: iframeData.isCrossOrigin,
    }),
    shallowEqual,
  );

  const postMessageToParent = useCallback(
    (message: any, targetOrigin = "*") => {
      if (!isIframe) {
        console.warn("Not in iframe, cannot post message to parent");
        return;
      }

      try {
        window.parent.postMessage(message, targetOrigin);
      } catch (e) {
        console.error("Failed to post message to parent:", e);
      }
    },
    [isIframe],
  );

  const listenToParentMessages = useCallback(
    (callback: (event: MessageEvent) => void, allowedOrigins?: string[]) => {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      if (!isIframe) return () => {};

      const handleMessage = (event: MessageEvent) => {
        if (allowedOrigins && !allowedOrigins.includes(event.origin)) {
          console.warn("Message from unauthorized origin:", event.origin);
          return;
        }
        callback(event);
      };

      window.addEventListener("message", handleMessage);
      return () => window.removeEventListener("message", handleMessage);
    },
    [isIframe],
  );

  return {
    postMessageToParent,
    listenToParentMessages,
    isIframe,
    isCrossOrigin,
  };
};

/**
 * Hook to listen for specific message types from parent
 */
export const useParentMessageListener = (
  messageType: string,
  callback: (data: any) => void,
  allowedOrigins?: string[],
) => {
  const { listenToParentMessages, isIframe } = useIframeMessaging();

  useEffect(() => {
    if (!isIframe) return;

    const cleanup = listenToParentMessages((event) => {
      if (event.data.type === messageType) {
        callback(event.data);
      }
    }, allowedOrigins);

    // eslint-disable-next-line consistent-return
    return cleanup;
  }, [isIframe, messageType, callback, allowedOrigins, listenToParentMessages]);
};

/**
 * Hook to track iframe navigation events
 */
export const useIframeNavigationTracking = (
  onNavigate?: (url: string, context: IframeState) => void,
) => {
  const context = useIframeDetection();

  const trackNavigation = useCallback(
    (url: string) => {
      if (onNavigate) {
        onNavigate(url, context);
      }

      if (typeof window !== "undefined" && (window as any).gtag) {
        (window as any).gtag("event", "iframe_navigation", {
          iframe_context: context.isIframe,
          destination_url: url,
          parent_origin: context.parentOrigin,
          is_cross_origin: context.isCrossOrigin,
          partner_domain: context.partnerDomain,
          partner_id: context.partnerId,
        });
      }
    },
    [context, onNavigate],
  );

  return { trackNavigation, context };
};

export default useIsIframe;
